import React, { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";
import AppointmentDialog from "@/components/AppointmentDialog";
import useSEO from "@/hooks/useSEO";

const faqs = [
  {
    cat: "General",
    items: [
      { q: "Do I need a referral to consult at Manas?", a: "No referral is required. You can book an appointment directly through our website, by phone, or by walking in during visiting hours." },
      { q: "Is my treatment confidential?", a: "Absolutely. All consultations and records are protected by strict patient confidentiality and only shared with the clinical team involved in your care." },
      { q: "Do you treat children and adolescents?", a: "Yes. We have specialists in child and adolescent psychiatry, and we work closely with families and schools when appropriate." },
      { q: "Can family members join the consultation?", a: "Yes, with your consent. We strongly encourage family involvement, especially for deaddiction and severe mood-disorder treatment plans." },
    ],
  },
  {
    cat: "Deaddiction",
    items: [
      { q: "Is the deaddiction programme residential or outpatient?", a: "Both. After your evaluation, we recommend the pathway most likely to succeed for your specific situation, with a typical residential stay of 14–28 days followed by an outpatient programme." },
      { q: "Will withdrawal be painful?", a: "Withdrawal is medically managed 24×7 by our physicians and is far more comfortable than going through it alone. Symptoms are minimised with medication." },
      { q: "What is the success rate?", a: "Long-term abstinence rates depend on multiple factors. With our integrated programme (medical + therapy + aftercare), most patients who complete the full course see significant improvement at the 1-year mark." },
    ],
  },
  {
    cat: "Neuromodulation",
    items: [
      { q: "Is rTMS painful?", a: "Most patients describe rTMS as a mild tapping sensation on the scalp. There is no anesthesia, and you remain awake and alert throughout the 30-minute session." },
      { q: "How long is the course?", a: "A typical acute course is 4–6 weeks, with sessions 5 days a week. Maintenance protocols may be recommended thereafter." },
      { q: "Will I need to stop my medication?", a: "No. rTMS is usually used alongside your current psychiatric medication. Any medication adjustments are made by your treating psychiatrist." },
      { q: "Who is not eligible for rTMS?", a: "Patients with metallic implants near the head, a history of seizures, or certain neurological conditions may not be candidates. We perform a thorough eligibility screening before starting." },
    ],
  },
  {
    cat: "Appointments & Billing",
    items: [
      { q: "How long are first consultations?", a: "First consultations are 45–60 minutes to allow a comprehensive history and assessment. Follow-up visits are usually 20–30 minutes." },
      { q: "Do you accept insurance?", a: "We provide detailed invoices that can be submitted to most insurance providers for reimbursement under outpatient or inpatient psychiatric care." },
      { q: "What is your cancellation policy?", a: "Please give us 24 hours' notice for cancellations or rescheduling so we can offer your slot to another patient." },
    ],
  },
];

export default function FAQ() {
  useSEO({
    title: "FAQ — Psychiatry, Deaddiction & Neuromodulation",
    description: "Common questions about consultations, confidentiality, deaddiction programmes, rTMS therapy and billing at Manas Neuropsychiatry Hospital, Bhavnagar.",
  });
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="faq-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <p className="eyebrow text-[#A39171] mb-6">Frequently Asked</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-4xl">
          Honest answers to <br /><span className="italic text-[#E07A5F]">very real questions</span>.
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-2xl leading-relaxed">
          If you can't find what you're looking for, please call or write to us — we'll respond
          personally, in confidence.
        </p>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10 space-y-16">
          {faqs.map((group, gi) => (
            <div key={group.cat} data-testid={`faq-group-${gi}`}>
              <p className="eyebrow text-[#E07A5F] mb-6">{group.cat}</p>
              <Accordion type="single" collapsible className="space-y-3">
                {group.items.map((it, i) => (
                  <AccordionItem
                    key={it.q}
                    value={`${gi}-${i}`}
                    data-testid={`faq-item-${gi}-${i}`}
                    className="bg-[#F4EFE6] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:bg-[#FDFBF7] data-[state=open]:border-[#E07A5F]/30 transition-colors"
                  >
                    <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5">
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                      {it.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="bg-[#2D2623] text-[#FDFBF7] rounded-[32px] p-10 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <p className="eyebrow text-[#E2C044] mb-4">Still wondering?</p>
                <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">
                  Speak with a clinician, <span className="italic text-[#E07A5F]">no obligation</span>.
                </h2>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3">
                <Button data-testid="faq-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white h-12 font-medium">
                  Book Consultation <ArrowUpRight className="ml-1 w-4 h-4" />
                </Button>
                <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] h-12 inline-flex items-center justify-center font-medium transition-colors">
                  Call us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
